import { AuthenticationService } from './core/services/auth.service';
import { AutoLogoutService } from './core/services/utelize/general/auto-logout-service';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { ConfigSystemParams, OurCompany, StorageValues } from './core/consts';
import { ConfigSystemService } from './core/services/utelize/system/config-system/config-system-service';
import { DecryptAesClientSide } from './core/encryption';
import { ExceptionsService } from './core/services/utelize/general/exceptions-service';
import { HttpClientService } from './core/services/utelize/general/http-client-service';
import { Router } from '@angular/router';
import { StorageService } from './core/services/utelize/general/storage-service';
import { VersionService } from './core/services/utelize/general/version-service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})

export class AppComponent implements AfterViewInit, OnDestroy, OnInit {

  private currentVersion!  : string;
  private intervalId       : any;
  private updatesIntervalId: any;

  public title: string=OurCompany.CompanyNamePascal;

  constructor(
    private authenticationService: AuthenticationService,
    private autoLogoutService    : AutoLogoutService, //CRN_20250919 - Although dulled, this is being used
    private configSystemService  : ConfigSystemService,
    private exceptionsService    : ExceptionsService,
    private httpClientService    : HttpClientService,
    private router               : Router,
    private storageService       : StorageService,
    private versionService       : VersionService
  ) {

  }

  public async ngOnInit(): Promise<void> {
    try {
      window.addEventListener("beforeunload", this.pageChangeOrBrowserClose);

      //CRN_20250919 - Load all the params before any further program execution
      //CRN_20260123 - This is now done in AppModule
      //await firstValueFrom(this.configSystemService.refreshConfigSystemValues());
      //this.encryptionService.serverSideEncryption=this.configSystemService.getValueAsBoolean(ConfigSystemParams.SecurityServerSideEncryption, false);

      this.applyPageBackground();

      this.currentVersion=this.versionService.version;
      
      //CRN_20250502 - User may have pressed Ctrl+F5 or Ctrl+Shift+R
      //               Log them out to ensure that they get the correct menus etc.
      if (DecryptAesClientSide(this.storageService.getItem(StorageValues.encForceLogout))=="true") {
        this.startLogOut();
        return;
      }

      //CRN_20250403 - Load all the Access Guids before any further program execution
      //CRN_20260501 - This is now done in AppModule
      //await this.loadAccessGuids();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit() {
    try {
      this.intervalId=setInterval(() => {
        if (this.configSystemService.getValueAsInteger(ConfigSystemParams.VersionSureSimPollingCheckMilliSecs, 0)>0) {

          //CRN_20250515 - All params MUST be loaded at this point 🤔 - CRN_20250919 - Wrong... 🤦‍♂️

          if (this.intervalId !== undefined) {
            clearInterval(this.intervalId);
          }

          this.checkForUpdatesInterval();
        }
      }, 1000);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy() {
    try {
      window.removeEventListener("beforeunload", this.pageChangeOrBrowserClose);

      if (this.updatesIntervalId)
        clearInterval(this.updatesIntervalId);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyPageBackground(): void {
    try {
      const useGrey=this.configSystemService.getValueAsBoolean(ConfigSystemParams.UseGreyPageBackground, true);
      document.documentElement.classList.toggle('ss-grey-bg', useGrey);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private checkForUpdatesInterval() {
    try {
      this.checkForUpdates();

      this.updatesIntervalId=setInterval(() => {
        this.checkForUpdates()
      }, this.configSystemService.getValueAsInteger(ConfigSystemParams.VersionSureSimPollingCheckMilliSecs, 0));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private checkForUpdates() {
    try {
      const cacheBuster: number=new Date().getTime();

      this.httpClientService.get<{ version: string }>(`assets/version.json?cb=${cacheBuster}`)
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        })
      )
      .subscribe({
        next: data => {
          if (data.version !== this.currentVersion) {
            console.log('New version detected. Reloading...');
            window.location.reload();
          }
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private startLogOut() {
    try {
      this.authenticationService.logoutApi()
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        })
      )
      .subscribe({
        next: data => {
          this.logOut();
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
          this.logOut();
        }})
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      this.logOut();
    }
  }

  private logOut() {
    try {
      this.authenticationService.logout();
      this.router.navigate(['/auth/login']);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private pageChangeOrBrowserClose=(): void => {
    try {
      if (this.configSystemService.getValueAsBoolean(ConfigSystemParams.SystemLocksFreeLocksOnPageChange, false)) {

        const userId=this.authenticationService?.currentUserObject()?.userId;

        if (userId && userId>0) {
          this.authenticationService.removeAllUserSystemLocks(userId);
        }
      }
    } catch (e) {
      this.exceptionsService.handleException(<Error>e);
    }
  };

  // public async loadAccessGuids(): Promise<void> {
  //   try {
  //     const curUser=this.authenticationService?.currentUserObject();
      
  //     if (!curUser || curUser.companyId<=0 || curUser.userId<=0) {
  //       this.componentAccessUserService.userAccessGuids=undefined;
  //       return;
  //     }
      
  //     this.componentAccessUserService.userAccessGuids=await firstValueFrom(this.componentAccessUserService.getAccessGuids(curUser.companyId, curUser.userId));
  //   }
  //   catch(e) {
  //     this.exceptionsService.handleException(<Error>e);
  //   }
  // }
}
